import * as React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Title3,
  Subtitle2,
  Body1,
  Caption1,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  left: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalXXXL,
    '@media (max-width: 768px)': {
      flex: 'none',
      width: '100%',
      padding: tokens.spacingHorizontalXL,
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '400px',
  },
  right: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorBrandBackground,
    padding: tokens.spacingHorizontalXXXL,
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  welcomeContent: {
    maxWidth: '650px',
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    alignItems: 'center',
  },
  welcomeTitle: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero1000,
    margin: 0,
  },
  welcomeSubtitle: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    opacity: 0.95,
    lineHeight: tokens.lineHeightBase500,
    margin: 0,
  },
  welcomeBody: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    opacity: 0.9,
  },
  welcomeFooter: {
    color: tokens.colorNeutralForegroundOnBrand,
    opacity: 0.7,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXL,
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    boxShadow: tokens.shadow16,
  },
  header: {
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  logo: {
    width: '120px',
    height: '120px',
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    marginBottom: tokens.spacingVerticalS,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
  },
  content: {
    padding: tokens.spacingVerticalL,
  },
  footer: {
    textAlign: 'center',
    paddingTop: tokens.spacingVerticalM,
  },
  link: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

interface ForgotPasswordLayoutProps {
  children: React.ReactNode;
}

const ForgotPasswordLayout: React.FC<ForgotPasswordLayoutProps> = ({ children }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.left}>
        <div className={styles.formContainer}>
          <Card className={styles.card}>
            <CardHeader className={styles.header}>
              <img
                src="/logo192.png"
                alt="Logo"
                className={styles.logo}
              />
              <Title3 className={styles.title}>Recuperação de Senha</Title3>
              <Subtitle2 className={styles.subtitle}>
                Digite seu e-mail para receber instruções de recuperação
              </Subtitle2>
            </CardHeader>
            <div className={styles.content}>
              {children}
            </div>
          </Card>
        </div>
      </div>
      <div className={styles.right}>
        <div className={styles.welcomeContent}>
          <Title3 className={styles.welcomeTitle}>
            Recuperação de Senha
          </Title3>
          <Body1 className={styles.welcomeBody}>
            Recupere o acesso à sua conta de forma segura e rápida.
            Siga as instruções enviadas por e-mail para redefinir sua senha.
          </Body1>
          <Caption1 className={styles.welcomeFooter}>
            Seus dados estão protegidos com criptografia avançada.
          </Caption1>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordLayout;